import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, MapPin, Phone } from "lucide-react";
import { MaskLines } from "./Reveal";
import { CONTACT } from "./data";
import { scrollToId } from "./SmoothScroll";

const D = 1.9;

const HeroPortrait = ({ mx, my, y }) => {
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 80, damping: 18 });
  const gx = useTransform(mx, [-0.5, 0.5], ["30%", "70%"]);
  return (
    <motion.div style={{ y }} className="relative mx-auto w-[78%] sm:w-[60%] lg:w-full max-w-[520px] [perspective:1200px]">
      <motion.div
        initial={{ clipPath: "inset(100% 0 0 0 round 999px 999px 0 0)" }}
        animate={{ clipPath: "inset(0% 0 0 0 round 999px 999px 0 0)" }}
        transition={{ duration: 1.6, delay: D + 0.2, ease: [0.76, 0, 0.24, 1] }}
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="relative aspect-[4/5] rounded-t-full bg-[#050505] border border-gold/30 overflow-hidden"
      >
        <motion.div style={{ left: gx }} className="absolute top-[18%] -translate-x-1/2 w-[80%] aspect-square rounded-full bg-gold/25 blur-[80px]" />
        <img src="/img/ig-hero.jpg" alt="Schiaritura degradè realizzata da Hair Boutique" className="absolute inset-0 w-full h-full object-cover object-center scale-[1.04]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 via-transparent to-transparent" />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, scale: 0.6, rotate: -30 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{ duration: 1.2, delay: D + 1, ease: [0.22, 1, 0.36, 1] }}
        className="absolute -left-6 sm:-left-10 bottom-10 w-24 h-24 sm:w-28 sm:h-28"
      >
        <svg viewBox="0 0 100 100" className="w-full h-full animate-spin-slow text-gold">
          <defs><path id="c" d="M50 50m-38 0a38 38 0 1 1 76 0a38 38 0 1 1-76 0" /></defs>
          <text className="font-mono" fontSize="8.5" letterSpacing="2.6" fill="currentColor"><textPath href="#c">PRENOTA • HAIR BOUTIQUE • </textPath></text>
        </svg>
        <span className="absolute inset-0 grid place-items-center text-gold font-display italic text-2xl">HB</span>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const wordX = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMove} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden pt-28 pb-16 lg:pt-32">
      <div className="absolute inset-0 spotlight pointer-events-none" />
      <motion.p style={{ x: wordX }} aria-hidden className="absolute bottom-[-4vw] left-0 whitespace-nowrap font-display italic text-[24vw] leading-none text-outline select-none pointer-events-none">
        Boutique Boutique
      </motion.p>

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: D, duration: 1 }} className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-gold">
            <span className="inline-block w-10 h-px bg-gold/70 align-middle mr-3" />Parrucchieri & Barbershop · Quartucciu
          </motion.p>
          <h1 data-testid="hero-title" className="mt-6 font-display text-[15vw] sm:text-7xl lg:text-[7.4rem] leading-[0.92] tracking-[-0.03em] text-ivory">
            <MaskLines onLoad delay={D} lines={["Capelli che", <span key="a" className="italic text-gold-grad">raccontano</span>, "chi sei."]} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: D + 0.6, duration: 1 }} className="mt-8 max-w-md text-sm sm:text-base text-ivory/75 leading-relaxed">
            Taglio, colore, barber e servizio sposa con prodotti professionali Olaplex, Lisap, ghd e Johnny Verax Professional. Entra in Hair Boutique ed esci con i capelli che hai sempre immaginato.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: D + 0.8, duration: 1 }} className="mt-10 flex flex-wrap items-center gap-4">
            <button data-testid="hero-cta-booking" onClick={() => scrollToId("prenota")} className="btn-gold">
              Richiedi appuntamento <ArrowUpRight className="w-4 h-4" />
            </button>
            <button data-testid="hero-cta-services" onClick={() => scrollToId("servizi")} className="btn-ghost">
              Scopri i servizi <ArrowDown className="w-4 h-4" />
            </button>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: D + 1.1, duration: 1 }} className="mt-10 flex flex-wrap gap-x-8 gap-y-3 font-mono text-[11px] uppercase tracking-[0.2em] text-ivory/70">
            <span className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-gold" />{CONTACT.address}, Quartucciu</span>
            <a data-testid="hero-phone-link" href={CONTACT.phoneHref} className="flex items-center gap-2 hover:text-gold transition-colors"><Phone className="w-3.5 h-3.5 text-gold" />{CONTACT.phone}</a>
          </motion.div>
        </div>
        <div className="lg:col-span-5">
          <HeroPortrait mx={mx} my={my} y={imgY} />
        </div>
      </div>
    </section>
  );
};
